import { useEffect } from "react";
import { useMutation } from "@apollo/client";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import store, { RootState } from "../../store";
import { NodeType, PageNode } from "../../types/PageNode";
import { setNodes } from "../../reducers/nodes";
import { setSubPageModal, setSubpagesLimitModal } from "../../reducers/modalSlice";
import { RequestPageType } from "../../types/RequestPageType";
import {
  GetPageQuery,
  usePageNewMutation,
} from "../Page/PageService.hooks";
import {
  changePageId,
  changeTitle,
  changeType,
  setSubPageLoading,
} from "../../reducers/app";
import useGetUrlLocale from "../../hooks/useGetUrlLocale";
import { useDuplicateContentPageForCommonPageMutation } from "../../components/context-menu/Template/UserTemplate/UserTemplates.hooks";
import { LIMIT_FREE } from "../../types/subscription";
import { useUserQuery } from "../User/UserService.hooks";
import { pageService } from "../Page/usePageService";
import { DELETE_PAGE } from "../../apollo/Mutations";
import { CONSTRUCTOR_EVENTS, gtagEvent } from "../../utils/analytics";
import BasicHeaderNodeFactory from "../Node/Factory/Header/BasicHeaderNodeFactory";
import { savePageContent } from "../post";
import { containsHeaderNode, removeNavItemByUrl, removeNodesFromHtml } from "../../utils/nodes/headerNode";
import { useGetPageLazyQuery } from "../Page/PageService.hooks";

type PageType = GetPageQuery["page"];

type SubpageData = {
  name: string;
  url: string;
  isKeep: boolean;
};

type SubpageTemplateData = SubpageData & {
  templateId: number;
  templatePageId: number;
};

export const useSubPageService = (page: PageType) => {
  const lng = useGetUrlLocale();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {data: userData} = useUserQuery()

  const [createSubPage, { loading }] = usePageNewMutation();
  const [duplicateContent] = useDuplicateContentPageForCommonPageMutation();
  const [deletePageMutation] = useMutation(DELETE_PAGE);
  const [getPage] = useGetPageLazyQuery({ fetchPolicy: "network-only" });
  const pageId = store.getState().appStorage.pageId;

  const previewState = useSelector(
    (state: RootState) => state.appStorage.preview,
  );
  const nodes = useSelector((state: RootState) => state.nodes.present.value);

  const isPremiumUser = userData?.user.activePremium || userData?.user?.admin;
  const rootPage = page?.rootPage ? page : page?.parentPage;
  const pageRootId = page?.rootPage ? rootPage?.id : rootPage?.id;
  const pageRootTitle = page?.rootPage ? rootPage?.title : rootPage?.title;
  const pageRootPublicUrl = page?.rootPage
    ? rootPage?.publicUrl
    : rootPage?.publicUrl;

  const createEmptyContent = (): PageNode => ({
      breadCrumbTitle: "Window",
      type: NodeType.PageNode,
      id: "1",
      parentId: null,
      tagName: "div",
      className: null,
      nodes: [],
      classes: [],
  });

  const subPages = page?.rootPage
    ? page?.subPages
    : page?.parentPage?.subPages || [];

  const openSubPageModal = () => {
    dispatch(setSubPageModal(true));
  };

  const selectSubPage = async (subPageId: number, subPageTitle: string) => {
    if (page?.id === subPageId) return;

    dispatch(setSubPageLoading(true));
    gtagEvent(CONSTRUCTOR_EVENTS.MANAGE_PAGES);
    navigate(`${lng}/${subPageId}`, { replace: true });
    dispatch(changePageId(subPageId));
    dispatch(changeTitle(subPageTitle));

    const newPage = await pageService.refetch(subPageId);
    const contentString = newPage?.contentString;
    const type = newPage?.type;

    if (type != null) {
      dispatch(changeType(type));
    }

    if (contentString && JSON.parse(contentString).length > 0) {
      dispatch(setNodes({ nodes: JSON.parse(contentString) }));
    } else {
      const emptyContent = createEmptyContent();
      emptyContent.customStyle =
        type === RequestPageType.MobilePage
          ? { "max-width": "440px", margin: "0px auto" }
          : {};
      dispatch(setNodes({ nodes: [emptyContent] }));
    }

    dispatch(setSubPageLoading(false));
  };

  const createSubpage = async (page: PageType, subpageData: SubpageData) => {
    return await createSubPage({
      variables: {
        title: subpageData?.name,
        url: subpageData?.url,
        type: page?.type,
        domainId: page?.domain.id,
        folderId: page?.folder?.id || null,
        seoTitle: page.seoTitle,
        seoKeyWords: page.seoKeywords,
        seoDescription: page.seoDescription,
        pageScript: page?.script,
        noIndex: page?.noIndex,
        copyParentPageContent: subpageData?.isKeep,
        parentPageId: page?.rootPage ? pageId : page?.parentPage?.id,
      },
    });
  };

  const createBlankSubpageContent = async (
    page: PageType,
    createdPage: { id?: number | null; title?: string | null },
    url: string,
  ) => {
    if (!createdPage.id) return;

    const root = createEmptyContent();
    const isMobilePage = page.type === RequestPageType.MobilePage;
    root.customStyle = isMobilePage
      ? { "max-width": "440px", margin: "0px auto" }
      : {};

    const createdPublicUrl = `${pageRootPublicUrl}/${url}`;
    const navigationPages = [
      rootPage,
      ...subPages,
      {
        id: createdPage.id,
        title: createdPage.title,
        publicUrl: createdPublicUrl,
      },
    ];

    root.nodes.push(
      BasicHeaderNodeFactory.getHeaderNode(root, navigationPages, isMobilePage),
    );
    await savePageContent(createdPage.id, [root]);
  };

  const deleteSubpage = async (id: number) => {
    await deletePageMutation({ variables: { id } });
  };

  const createSubpageByTemplate = async (
    page: PageType,
    subpageData: SubpageTemplateData,
  ) => {
    if(!isPremiumUser && subPages.length >= LIMIT_FREE.SUBPAGES){
      dispatch(setSubpagesLimitModal(true))
      return null
    }

    dispatch(setSubPageLoading(true));

    let createdPageId: number | null = null;
    try {
      const { data: subpageEmpty } = await createSubpage(page, subpageData);

      if (!subpageEmpty?.pageNew?.id) return null;

      createdPageId = subpageEmpty.pageNew.id;

      const { data: duplicateResult } = await duplicateContent({ variables: {
        idPageFrom: subpageData.templatePageId,
        idPageTo: createdPageId,
      }});

      const contentString = duplicateResult?.duplicateContentPageForCommonPage?.contentString;
      if (!contentString) throw new Error("Template content copy returned empty result");

      const templateNodes = JSON.parse(contentString) as PageNode[];
      const root = templateNodes[0];
      if (root) {
        root.subPageTemplateId = subpageData.templateId;
        if (!containsHeaderNode(root)) {
          const isMobilePage = page.type === RequestPageType.MobilePage;
          const createdPublicUrl = `${pageRootPublicUrl}/${subpageData.url}`;
          const navigationPages = [
            rootPage,
            ...subPages,
            { id: createdPageId, title: subpageData.name, publicUrl: createdPublicUrl },
          ];
          root.nodes.unshift(BasicHeaderNodeFactory.getHeaderNode(root, navigationPages, isMobilePage));
        }
      }
      await savePageContent(createdPageId, templateNodes);
      return subpageEmpty.pageNew;
    } catch (error) {
      if (createdPageId) {
        await deletePageMutation({ variables: { id: createdPageId } }).catch((e: unknown) => {
          console.error("Failed to rollback created subpage", createdPageId, e);
        });
      }
      throw error;
    } finally {
      dispatch(setSubPageLoading(false));
    }
  }

  useEffect(() => {
    const subpageActions =
      document.querySelectorAll<HTMLElement>(".subpage-class");

    const clickHandler = async (event: Event) => {
      if (!previewState) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const url = (event.currentTarget as HTMLElement).dataset.url;
      const currentSubPage = [...subPages, rootPage].find(
        (subpage) => subpage?.publicUrl === url,
      );

      if (!currentSubPage?.id) return;

      await selectSubPage(currentSubPage.id, currentSubPage.title);
    };
    if (previewState) {
      subpageActions.forEach((subpageAction) => {
        subpageAction.addEventListener("click", clickHandler);
      });
    } else {
      subpageActions.forEach((subpageAction) => {
        subpageAction.removeEventListener("click", clickHandler);
      });
    }
    return () => {
      subpageActions.forEach((subpageAction) => {
        subpageAction.removeEventListener("click", clickHandler);
      });
    };
  }, [nodes, previewState]);

  const removeSubpageFromAllHeaders = async (deletedPageId: number, deletedPagePublicUrl: string) => {
    const pagesToUpdate = [rootPage, ...subPages].filter(
      (p) => p?.id && p.id !== deletedPageId,
    ) as { id: number }[];

    await Promise.all(
      pagesToUpdate.map(async (p) => {
        try {
          const result = await getPage({ variables: { id: p.id } });
          const contentString = result?.data?.page?.contentString;
          if (!contentString) return;

          const pageNodes = JSON.parse(contentString) as PageNode[];
          const removedIds = pageNodes[0] ? removeNavItemByUrl(pageNodes[0], deletedPagePublicUrl) : [];
          if (!removedIds.length) return;

          const prePublishContent = removeNodesFromHtml(
            result?.data?.page?.prePublishContentString ?? "",
            removedIds,
          );
          await savePageContent(p.id, pageNodes, prePublishContent);
        } catch (err) {
          console.error("Failed to remove nav item from page", p.id, err);
        }
      }),
    );
  };

  return {
    pageRootId,
    pageRootTitle,
    pageRootPublicUrl,
    subPages,
    openSubPageModal,
    selectSubPage,
    createSubpage,
    createSubpageByTemplate,
    createBlankSubpageContent,
    deleteSubpage,
    removeSubpageFromAllHeaders,
    loading: loading
  };
};
